import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, fmtINR } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, FileText } from "lucide-react";

const Stat = ({ label, value, tone }) => (
  <div className="bg-white border border-[#E6E4DD] rounded-sm p-5">
    <span className="text-xs uppercase tracking-[0.1em] text-[#8CA3A7]">{label}</span>
    <div className={`font-mono text-xl tabular-nums mt-3 ${tone || "text-[#0A0D14]"}`}>{value}</div>
  </div>
);

export default function CategoryReport() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const category = params.get("category") || "";
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/reports/category", { params: { category } })
      .then((r) => setData(r.data)).catch(() => setData({ sellers: [] }));
  }, [category]);

  const downloadPdf = async () => {
    try {
      const res = await api.get("/reports/category/pdf", { params: { category }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `category-${category}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Report failed."); }
  };

  if (!data) return <div className="text-slate-400 font-mono text-sm">Loading report…</div>;

  return (
    <div className="space-y-6" data-testid="category-report-page">
      <button onClick={() => navigate("/")} className="text-sm text-[#8CA3A7] flex items-center gap-1 hover:text-[#1C2331] transition-colors">
        <ArrowLeft className="w-4 h-4" /> Back to dashboard
      </button>

      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-[#8CA3A7]">Category Seller</p>
          <h1 className="font-heading text-4xl font-bold text-[#0A0D14] tracking-tight mt-1" data-testid="category-report-name">{category}</h1>
          <p className="text-sm text-[#8CA3A7] mt-2">Sub-seller-wise, further divided buyer-wise.</p>
        </div>
        <button data-testid="category-report-pdf-btn" onClick={downloadPdf} className="text-sm bg-white border border-[#E6E4DD] rounded-sm px-3 py-2 font-medium flex items-center gap-1.5 hover:border-[#1C2331] transition-colors shrink-0">
          <FileText className="w-3.5 h-3.5" /> Download PDF
        </button>
      </div>

      <div className="grid grid-cols-3 gap-6">
        <Stat label="Orders" value={data.order_count} />
        <Stat label="Total Value" value={fmtINR(data.total_value)} />
        <Stat label="Outstanding" value={fmtINR(data.outstanding)} tone={data.outstanding > 0 ? "text-[#D95D39]" : "text-[#065F46]"} />
      </div>

      <div className="space-y-6">
        {(data.sellers || []).map((s) => (
          <div key={s.seller} data-testid={`category-seller-${s.seller}`} className="border border-[#E6E4DD] rounded-sm bg-white overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-[#E6E4DD]">
              <span className="font-heading font-semibold text-[#0A0D14]">{s.seller}</span>
              <span className="font-mono text-sm text-[#0A0D14]">{fmtINR(s.value)} <span className="text-[#D95D39]">· {fmtINR(s.outstanding)} due</span></span>
            </div>
            <table className="w-full text-sm text-left">
              <thead>
                <tr className="font-mono text-[10px] uppercase tracking-wider text-[#8CA3A7] border-b border-[#E6E4DD]">
                  <th className="px-4 py-2">Buyer</th>
                  <th className="px-4 py-2 text-right">Orders</th>
                  <th className="px-4 py-2 text-right">Value</th>
                  <th className="px-4 py-2 text-right">Outstanding</th>
                </tr>
              </thead>
              <tbody>
                {s.buyers.map((b) => (
                  <tr key={b.buyer} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60 cursor-pointer" onClick={() => navigate(`/parties/detail?name=${encodeURIComponent(b.buyer)}&role=buyer`)}>
                    <td className="px-4 py-2 text-[#0A0D14]">{b.buyer}</td>
                    <td className="px-4 py-2 text-right font-mono tabular-nums text-[#4B5563]">{b.order_count}</td>
                    <td className="px-4 py-2 text-right font-mono tabular-nums text-[#0A0D14]">{fmtINR(b.value)}</td>
                    <td className="px-4 py-2 text-right font-mono tabular-nums text-[#D95D39]">{fmtINR(b.outstanding)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
        {(data.sellers || []).length === 0 && (
          <div className="bg-white border border-[#E6E4DD] rounded-sm p-12 text-center text-sm text-slate-400">No orders for this category.</div>
        )}
      </div>
    </div>
  );
}
